import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchVendorById, fetchEventsByVendorId, recordPageView } from '../supabaseClient';
import { FaTh, FaList, FaCalendarAlt, FaHistory, FaChevronLeft, FaChevronRight, FaBan } from 'react-icons/fa';
import { MdLocationOn } from 'react-icons/md';
import Layout from '../components/Layout';

const SPORTS_EVENTS = [
  'Basketball',
  'Football',
  'Soccer',
  'Baseball',
  'Softball',
  'Volleyball',
  'Track & Field',
  'Wrestling',
  'Tennis',
  'Golf',
  'Swimming',
  'Cross Country',
];

const defaultSportsImageUrl = "https://s3.amazonaws.com/com.ticketspicket.app/activity-images/basketball_Edited.jpg";
const defaultEventImageUrl = "https://www.pncfairfaxconnection.com/wp-content/themes/pnc-child/img/event_placeholder.jpg";



const isCancelledEvent = (event) => {
  return event.isCancelled || event.is_cancelled;
};


const areTicketsAvailable = (event) => {
  if (!event.tickets_sale_start_date) return true; 
  const now = new Date();
  const saleStartDate = new Date(event.tickets_sale_start_date);
  return now >= saleStartDate;
};

const formatTicketSaleDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

function VendorPage() {
  const [vendor, setVendor] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isListView, setIsListView] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 6;
  const { id } = useParams();


  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = events.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(events.length / eventsPerPage);


  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, isListView]);

  const Pagination = () => (
    <div className="flex justify-center items-center space-x-4 mt-8">
      <button
        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
        className={`p-2 rounded-full transition-colors duration-200 ${
          currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Previous page"
      >
        <FaChevronLeft className="w-5 h-5" />
      </button>
      
      <div className="flex items-center space-x-2">
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index + 1}
            onClick={() => setCurrentPage(index + 1)}
            className={`w-8 h-8 rounded-full ${
              currentPage === index + 1
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>

      <button
        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-full transition-colors duration-200 ${
          currentPage === totalPages
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Next page"
      >
        <FaChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  useEffect(() => {
    const loadVendorData = async () => {
      try {
        if (!vendor) {
          const viewResult = await recordPageView(id);
          if (viewResult.error) console.error('Error recording view:', viewResult.error);
        }
        
        if (!vendor) {
          const vendorData = await fetchVendorById(id);
          if (vendorData.error) throw vendorData.error;
          setVendor(vendorData.data);
        }
      } catch (error) {
        console.error('Error loading vendor data:', error);
        setError(error.message);
      }
    };

    if (id) {
      loadVendorData();
    }
  }, [id, vendor]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const includePast = activeTab === 'past';
        const eventsData = await fetchEventsByVendorId(id, includePast);
        if (eventsData.error) throw eventsData.error;
        setEvents(eventsData.data || []);
      } catch (error) {
        console.error('Error loading events:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadEvents();
    }
  }, [id, activeTab]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;
  if (!vendor) return <p>Vendor not found</p>;

  return (
    <Layout vendorData={vendor}> 
      <div className="min-h-screen bg-gray-100">
        
        {/* Secondary Banner */}
        <section
          className="text-center text-white py-28"
          style={{
            background: `
              repeating-radial-gradient(circle at 10px 10px, ${vendor.primary_color}, ${vendor.primary_color} 10px, ${vendor.secondary_color} 10px, ${vendor.secondary_color} 20px)
            `,
          }}
        >
          <h3 className="text-4xl font-bold">{vendor.company_name}</h3>
          <h1 className="text-2xl font-semibold">{vendor.mascotname}</h1>
        </section>

        {/* Events Section */}
        <main className="flex justify-center items-center py-8">
          <div className="w-full max-w-6xl bg-white p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
              <h2 className="text-3xl font-bold mb-4 sm:mb-0">EVENTS</h2>
              <div className="flex bg-gray-100 rounded-lg p-1 self-start">
                <button
                  onClick={() => setActiveTab('upcoming')}
                  className={`px-3 py-2 md:px-4 text-sm md:text-base rounded-md transition-colors duration-200 ${
                    activeTab === 'upcoming'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Upcoming Events
                </button>
                <button
                  onClick={() => setActiveTab('past')}
                  className={`px-3 py-2 md:px-4 text-sm md:text-base rounded-md transition-colors duration-200 ${
                    activeTab === 'past'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Past Events
                </button>
              </div>
            </div>

            {events.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  {activeTab === 'upcoming' ? (
                    <FaCalendarAlt className="w-8 h-8 text-gray-400" />
                  ) : (
                    <FaHistory className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {activeTab === 'upcoming' ? 'No Upcoming Events' : 'No Past Events'}
                </h3>
                <p className="text-gray-600">
                  {activeTab === 'upcoming' 
                    ? 'Check back later for new events.'
                    : 'Past events will appear here once events are completed.'
                  }
                </p>
              </div>
            )}

            {events.length > 0 && (
              <>
                <div className="flex justify-end mb-4">
                  <button
                    className={`p-2 ${!isListView ? 'text-white' : 'text-gray-600'}`}
                    style={{ backgroundColor: !isListView ? vendor.primary_color : 'transparent' }}
                    onClick={() => setIsListView(false)}
                  >
                    <FaTh />
                  </button>
                  <button
                    className={`p-2 ${isListView ? 'text-white' : 'text-gray-600'}`}
                    style={{ backgroundColor: isListView ? vendor.primary_color : 'transparent' }}
                    onClick={() => setIsListView(true)}
                  >
                    <FaList />
                  </button>
                </div>

                <div className={`grid ${isListView ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
                  {currentEvents.map((event) => {
                    const eventIsPast = activeTab === 'past'; 
                    const eventIsCancelled = isCancelledEvent(event);
                    const ticketsAvailable = areTicketsAvailable(event);
                    const isClickable = !eventIsPast && !eventIsCancelled && ticketsAvailable;
                    
                    const EventCard = () => (
                      <div
                        className={`bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200 ${
                          isListView ? 'p-4 flex flex-col justify-between' : 'relative'
                        } ${eventIsCancelled || !ticketsAvailable ? 'opacity-75' : ''}`}
                      >
                        {isListView ? (
                          <>
                            {/* Logo and Event Details */}
                            <div className="flex items-start">
                              <img
                                src={vendor.logoURL}
                                alt="Vendor Logo"
                                className="w-12 h-12 rounded-full bg-white p-1 mr-4 flex-shrink-0"
                              />
                              <div className="flex-grow min-w-0">
                                <h3 className={`text-lg font-bold truncate ${eventIsCancelled ? 'line-through text-gray-600' : ''}`}>
                                  {SPORTS_EVENTS.includes(event.event_type) 
                                    ? `${vendor.short_name} vs ${event.opponent}`
                                    : event.title
                                  }
                                </h3>
                                {eventIsCancelled && (
                                  <div className="flex items-center text-red-600 text-sm mb-1">
                                    <FaBan className="mr-1" />
                                    <span className="font-medium">Event Cancelled</span>
                                  </div>
                                )}
                                {!eventIsCancelled && !ticketsAvailable && (
                                  <div className="flex items-center text-orange-600 text-sm mb-1">
                                    <FaCalendarAlt className="mr-1" />
                                    <span className="font-medium">Tickets available {formatTicketSaleDate(event.tickets_sale_start_date)}</span>
                                  </div>
                                )}
                                <p className={`text-gray-600 mb-2 ${eventIsCancelled ? 'line-through' : ''}`}>
                                  {SPORTS_EVENTS.includes(event.event_type)
                                    ? `${event.gender_type} ${event.event_type}`
                                    : event.event_type
                                  }
                                </p>
                                <div className={`flex items-center text-gray-500 text-sm ${eventIsCancelled ? 'line-through' : ''}`}>
                                  <MdLocationOn className="mr-1 flex-shrink-0" />
                                  <span className="truncate">{event.location}</span>
                                </div>
                              </div>
                              {/* Date Box */}
                              <div
                                className="text-white p-2 text-center rounded-lg ml-auto"
                                style={{ backgroundColor: eventIsCancelled ? '#dc2626' : (vendor.primary_color || '#0000FF') }}
                              >
                                {eventIsCancelled && (
                                  <FaBan className="text-sm mx-auto mb-1" />
                                )}
                                <span className="block text-sm font-bold">
                                  {new Date(event.start_date).toLocaleString('en-US', { 
                                    weekday: 'short',
                                    hour12: true,
                                  })}
                                </span>
                                <span className="block text-xl font-bold">
                                  {new Date(event.start_date).toLocaleString('en-US', { 
                                    day: '2-digit', 
                                    month: 'short',
                                  })}
                                </span>
                                <span className="block text-sm font-semibold mt-1">
                                  {new Date(event.start_date).toLocaleString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true,
                                  })}
                                </span>
                              </div>
                            </div>
                            {/* Buy Tickets Section with Border */}
                            <div className="border-t pt-4 mt-4 flex justify-between items-center">
                              <div className="text-gray-500 text-2xl">
                                <i className="fas fa-basketball-ball"></i>
                              </div>
                              <span className={`font-semibold ${
                                eventIsPast
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : eventIsCancelled
                                  ? 'text-red-500 cursor-not-allowed'
                                  : !ticketsAvailable
                                  ? 'text-orange-500 cursor-not-allowed'
                                  : 'text-blue-600 hover:underline'
                              }`}>
                                {eventIsPast 
                                  ? 'Event Ended' 
                                  : eventIsCancelled 
                                  ? 'Event Cancelled'
                                  : !ticketsAvailable
                                  ? 'Tickets Not Available Yet'
                                  : 'Buy Tickets →'}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Image Section with Default Image Fallback */}
                            <div className="relative">
                              <img
                                src={event.default_image?.image_path || 
                                  (SPORTS_EVENTS.includes(event.event_type) ? defaultSportsImageUrl : defaultEventImageUrl)
                                }
                                alt={event.title}
                                className={`w-full h-48 object-cover ${eventIsCancelled || !ticketsAvailable ? 'grayscale' : ''}`}
                              />
                              {eventIsCancelled && (
                                <div className="absolute inset-0 bg-red-600 bg-opacity-20 flex items-center justify-center">
                                  <div className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold">
                                    <FaBan className="inline mr-2" />
                                    CANCELLED
                                  </div>
                                </div>
                              )}
                              {!eventIsCancelled && !ticketsAvailable && (
                                <div className="absolute inset-0 bg-orange-600 bg-opacity-20 flex items-center justify-center z-10">
                                  <div className="bg-orange-600 text-white px-3 py-2 rounded-lg font-bold text-center">
                                    <FaCalendarAlt className="inline mr-2" />
                                    <div className="text-xs">TICKETS AVAILABLE</div>
                                    <div className="text-xs">{formatTicketSaleDate(event.tickets_sale_start_date)}</div>
                                  </div>
                                </div>
                              )}
                              <img
                                src={vendor.logoURL}
                                alt="Vendor Logo"
                                className="absolute top-2 left-2 w-10 h-10 rounded-full bg-white p-1"
                              />
                              <div
                                className="absolute top-2 right-2 text-white p-2 text-center rounded-lg"
                                style={{ backgroundColor: eventIsCancelled ? '#dc2626' : (vendor.primary_color || '#0000FF') }}
                              >
                                {eventIsCancelled && (
                                  <FaBan className="text-sm mx-auto mb-1" />
                                )}
                                <span className="block text-sm font-bold">
                                  {new Date(event.start_date).toLocaleString('en-US', { 
                                    weekday: 'short',
                                    hour12: true,
                                  })}
                                </span>
                                <span className="block text-xl font-bold">
                                  {new Date(event.start_date).toLocaleString('en-US', { 
                                    day: '2-digit', 
                                    month: 'short',
                                  })}
                                </span>
                                <span className="block text-sm font-semibold mt-1">
                                  {new Date(event.start_date).toLocaleString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true,
                                  })}
                                </span>
                              </div>
                            </div>
                            {/* Content Section */}
                            <div className="p-4">
                              <h3 className={`text-lg font-bold truncate ${eventIsCancelled ? 'line-through text-gray-600' : ''}`}>
                                {SPORTS_EVENTS.includes(event.event_type) 
                                  ? `${vendor.short_name} vs ${event.opponent}`
                                  : event.title
                                }
                              </h3>
                              {eventIsCancelled && (
                                <div className="flex items-center text-red-600 text-sm mb-2">
                                  <FaBan className="mr-1" />
                                  <span className="font-medium">Event Cancelled</span>
                                </div>
                              )}
                              {!eventIsCancelled && !ticketsAvailable && (
                                <div className="flex items-center text-orange-600 text-sm mb-2">
                                  <FaCalendarAlt className="mr-1" />
                                  <span className="font-medium">Tickets available {formatTicketSaleDate(event.tickets_sale_start_date)}</span>
                                </div>
                              )}
                              <p className={`text-gray-600 mb-2 ${eventIsCancelled ? 'line-through' : ''}`}>
                                {SPORTS_EVENTS.includes(event.event_type)
                                  ? `${event.gender_type} ${event.event_type}`
                                  : event.event_type
                                }
                              </p>
                              <div className={`flex items-center text-gray-500 text-sm mb-4 ${eventIsCancelled ? 'line-through' : ''}`}>
                                <MdLocationOn className="mr-1 flex-shrink-0" />
                                <span className="truncate">{event.location}</span>
                              </div>
                              <div className="border-t pt-4 mt-auto flex justify-between items-center">
                                <div className="text-gray-500 text-2xl">
                                  <i className="fas fa-basketball-ball"></i>
                                </div>
                                <span className={`font-semibold ${
                                  eventIsPast
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : eventIsCancelled
                                    ? 'text-red-500 cursor-not-allowed'
                                    : !ticketsAvailable
                                    ? 'text-orange-500 cursor-not-allowed'
                                    : 'text-blue-600 hover:underline'
                                }`}>
                                  {eventIsPast 
                                    ? 'Event Ended' 
                                    : eventIsCancelled 
                                    ? 'Event Cancelled'
                                    : !ticketsAvailable
                                    ? 'Tickets Not Available Yet'
                                    : 'Buy Tickets'}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );

                    return (
                      <div key={event.id}>
                        {isClickable ? (
                          <Link to={`/event-page/${event.id}`} className="block">
                            <EventCard />
                          </Link>
                        ) : (
                          <EventCard />
                        )}
                      </div>
                    );
                  })}
                </div>
                {events.length > eventsPerPage && <Pagination />}
              </>
            )}
          </div>
        </main>
      </div>
    </Layout>
  );
}

export default VendorPage;
